import { Layer, Line, Rect } from "react-konva";
import { useEffect, useMemo, useRef, useState } from "react";
import type { KonvaEventObject } from "konva/lib/Node";
import type { Opening, Room, Vec2, WallMount } from "../../lib/types";
import { classifyRoomSketch } from "../../lib/classifyRoomSketch";
import type { FitInfo } from "../../lib/useFitToRoom";
import {
  processStroke,
  processWallStroke,
  unionWithRoom,
  SKETCH_ADDITION_CLOSE_THRESHOLD_IN,
  SKETCH_ADDITION_MIN_AREA_SQ_IN,
  SKETCH_ADDITION_MIN_RDP_TOLERANCE_IN,
  SKETCH_ADDITION_RESAMPLE_SPACING_IN,
  type ProcessStrokeOptions,
  type SketchResult,
} from "../../lib/sketch";

/** Outcome handed back to the parent once a stroke is processed. */
export interface SketchOutcome {
  /** Final polygon (post-union if in appendMode). Applied via `setRoomPoints`. */
  points: Vec2[] | null;
  /** Status copy to surface in the confirm bar. */
  message: string;
  /** Pipeline warnings ("Snapped to 90° corners", etc.). */
  warnings: string[];
  /** Whether the snap-to-nearest-wall retry was needed in appendMode. */
  snapped?: boolean;
  /** True iff `processStroke` succeeded; false for too-small / self-intersecting. */
  ok: boolean;
  /** Set when the unified extend tool classified the stroke. */
  kind?: "addition" | "wall";
}

interface Props {
  /** Stage size in CSS pixels. */
  width: number;
  height: number;
  /** The Stage's pan and zoom. The capture rect and guide lines cover
   *  whatever part of the stage is on screen. */
  view: { x: number; y: number; scale: number };
  /** Px ↔ inches converter from `useFitToRoom`. */
  fit: FitInfo;
  /** Existing room polygon (only consulted when `appendMode` is true). */
  roomPoints: Vec2[];
  /** When true, the stroke is unioned into the existing room. */
  appendMode?: boolean;
  /** When true, the stroke is an open interior wall, not a closed outline. */
  wallMode?: boolean;
  /** When true, the stroke is classified as an addition or an interior wall. */
  extendMode?: boolean;
  /** Room the extend classifier commits against. Required when `extendMode`. */
  room?: Room;
  openings?: Opening[];
  mounts?: WallMount[];
  /** When true, the overlay stops capturing pointer events so the parent
   *  Stage can receive drag events instead (Space-held pan in the canvas).
   *  Any in-progress stroke is canceled when this flips on. */
  panMode?: boolean;
  /** Extra `processStroke` overrides merged into the non-append branch.
   *  The room sketch leaves this undefined (full-room thresholds); the
   *  furniture creator passes smaller min-area / resample / close thresholds
   *  so a foot-scale piece isn't rejected as "too small". */
  processOptions?: Partial<ProcessStrokeOptions>;
  /** Receives the final result so the parent can update the store & UI. */
  onResult: (outcome: SketchOutcome) => void;
}

/** Minimum spacing (in scene px) between captured points. Below this we
 *  discard the new sample to keep the stroke smooth and the array short.
 *  Resampling happens later in the pipeline; this is just a fast first pass. */
const MIN_SAMPLE_SPACING_PX = 1.5;

/** How long the raw stroke is treated as a recent segment for snap hints. */
const HINT_TAIL_PX = 28;

/** Show horizontal/vertical guide lines when the tail of the stroke is
 *  within this many degrees of an axis. */
const HINT_ANGLE_TOLERANCE_DEG = 22.5;

export function SketchOverlay({
  width,
  height,
  view,
  fit,
  roomPoints,
  appendMode = false,
  wallMode = false,
  extendMode = false,
  room,
  openings = [],
  mounts = [],
  panMode = false,
  processOptions,
  onResult,
}: Props) {
  const [rawPx, setRawPx] = useState<Vec2[] | null>(null);
  const [freeAngles, setFreeAngles] = useState(false);
  const [hint, setHint] = useState<{ horizontal: boolean; vertical: boolean }>(
    { horizontal: false, vertical: false }
  );
  const drawingRef = useRef(false);

  // Stop listening to keyboard once the component unmounts (e.g. user
  // toggles back to Edit mid-stroke).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Shift") setFreeAngles(true);
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.key === "Shift") setFreeAngles(false);
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  function getPointer(e: KonvaEventObject<unknown>): Vec2 | null {
    const stage = e.target.getStage();
    if (!stage) return null;
    const p = stage.getRelativePointerPosition();
    if (!p) return null;
    return { x: p.x, y: p.y };
  }

  /** A second finger means pinch: drop the stroke and let the event reach
   *  the Stage's pinch handler. */
  function multiTouch(e: KonvaEventObject<unknown>): boolean {
    const touches = (e.evt as TouchEvent | undefined)?.touches;
    if (!touches || touches.length < 2) return false;
    if (drawingRef.current) {
      drawingRef.current = false;
      setRawPx(null);
      setHint({ horizontal: false, vertical: false });
    }
    return true;
  }

  function startStroke(e: KonvaEventObject<unknown>) {
    if (multiTouch(e)) return;
    e.cancelBubble = true;
    const p = getPointer(e);
    if (!p) return;
    const native = e.evt as { shiftKey?: boolean } | undefined;
    if (native?.shiftKey) setFreeAngles(true);
    drawingRef.current = true;
    setRawPx([p]);
  }

  function extendStroke(e: KonvaEventObject<unknown>) {
    if (multiTouch(e) || !drawingRef.current) return;
    e.cancelBubble = true;
    const p = getPointer(e);
    if (!p) return;
    setRawPx((prev) => {
      if (!prev) return [p];
      const last = prev[prev.length - 1];
      const dx = p.x - last.x;
      const dy = p.y - last.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < MIN_SAMPLE_SPACING_PX * MIN_SAMPLE_SPACING_PX) return prev;
      // Update snap hint based on the tail of the stroke.
      let tail: Vec2 | null = null;
      let cum = 0;
      for (let i = prev.length - 1; i > 0; i--) {
        const a = prev[i];
        const b = prev[i - 1];
        cum += Math.hypot(a.x - b.x, a.y - b.y);
        if (cum >= HINT_TAIL_PX) {
          tail = b;
          break;
        }
      }
      const ref = tail ?? prev[0];
      const tdx = p.x - ref.x;
      const tdy = p.y - ref.y;
      if (Math.hypot(tdx, tdy) > 4) {
        const angle = Math.atan2(tdy, tdx) * (180 / Math.PI);
        const norm = ((angle % 180) + 180) % 180; // 0..180
        const horizontal =
          norm <= HINT_ANGLE_TOLERANCE_DEG ||
          norm >= 180 - HINT_ANGLE_TOLERANCE_DEG;
        const vertical = Math.abs(norm - 90) <= HINT_ANGLE_TOLERANCE_DEG;
        setHint({ horizontal, vertical });
      }
      return [...prev, p];
    });
  }

  function endStroke(e: KonvaEventObject<unknown>) {
    if (!drawingRef.current) return;
    e.cancelBubble = true;
    drawingRef.current = false;
    const raw = rawPx ?? [];
    setRawPx(null);
    setHint({ horizontal: false, vertical: false });
    if (raw.length < 3) return;

    // Convert px → inches.
    const inInches: Vec2[] = raw.map((p) => fit.pxToRoom(p));
    if (extendMode && room) {
      const classified = classifyRoomSketch(room, inInches, openings, mounts, {
        freeAngles,
      });
      if (!classified.ok) {
        onResult({
          ok: false,
          points: null,
          message: classified.message,
          warnings: [],
        });
        return;
      }
      onResult({
        ok: true,
        kind: classified.kind,
        points: classified.points,
        message: classified.message,
        warnings: classified.warnings,
        snapped: classified.kind === "addition" ? classified.snapped : undefined,
      });
      return;
    }
    // Alcoves/closets are typically much smaller than a whole room (a 24×36"
    // closet is ~6 ft²), so the full-room defaults — 16 ft² minimum area, 4"
    // resample spacing, 3" RDP floor — would reject every realistic addition
    // as "too small" or "stroke too short". Drop those floors when appending.
    if (wallMode) {
      const wall = processWallStroke(inInches, { freeAngles });
      if (!wall.ok) {
        onResult({
          ok: false,
          points: null,
          message: wall.message,
          warnings: [],
        });
        return;
      }
      onResult({
        ok: true,
        points: wall.points,
        message: wall.warnings[0] ?? "Snapped to a clean wall",
        warnings: wall.warnings,
      });
      return;
    }

    const opts: ProcessStrokeOptions = { freeAngles };
    if (appendMode) {
      opts.resampleSpacing = SKETCH_ADDITION_RESAMPLE_SPACING_IN;
      opts.minRdpToleranceIn = SKETCH_ADDITION_MIN_RDP_TOLERANCE_IN;
      opts.minAreaSqIn = SKETCH_ADDITION_MIN_AREA_SQ_IN;
      opts.closeThresholdIn = SKETCH_ADDITION_CLOSE_THRESHOLD_IN;
    } else if (processOptions) {
      // Caller-supplied overrides (e.g. the furniture creator's small-piece
      // thresholds). `freeAngles` from the live Shift state still wins unless
      // the caller explicitly overrides it.
      Object.assign(opts, processOptions);
    }
    const result: SketchResult = processStroke(inInches, opts);
    if (!result.ok) {
      onResult({
        ok: false,
        points: null,
        message: result.message,
        warnings: [],
      });
      return;
    }

    if (appendMode) {
      const union = unionWithRoom(roomPoints, result.points);
      if (!union.ok) {
        onResult({
          ok: false,
          points: null,
          message:
            union.reason === "disjoint"
              ? "Draw the addition so it touches a wall of the room."
              : "That addition crosses itself — try again.",
          warnings: result.warnings,
        });
        return;
      }
      const snapWarning = union.snapped
        ? "Snapped addition to the nearest wall"
        : "Addition merged into the room";
      onResult({
        ok: true,
        points: union.points,
        message: snapWarning,
        warnings: result.warnings,
        snapped: union.snapped,
      });
      return;
    }

    onResult({
      ok: true,
      points: result.points,
      message:
        result.warnings[0] ??
        (freeAngles ? "Kept your free angles" : "Snapped to clean walls"),
      warnings: result.warnings,
    });
  }

  // ---- Visuals ----

  const rawFlat = useMemo(() => {
    if (!rawPx || rawPx.length < 2) return null;
    const arr: number[] = [];
    for (const p of rawPx) arr.push(p.x, p.y);
    return arr;
  }, [rawPx]);

  const last = rawPx && rawPx.length > 0 ? rawPx[rawPx.length - 1] : null;

  // The on-screen window in stage-local px: the Stage transform inverted.
  const k = view.scale > 0 && Number.isFinite(view.scale) ? view.scale : 1;
  const left = -view.x / k;
  const top = -view.y / k;
  const right = left + width / k;
  const bottom = top + height / k;

  return (
    <Layer>
      {/* Invisible hit target over the visible window so we capture pointer
          events even where there are no other shapes. Disabled in pan mode so
          the Stage can receive drag events for Space-to-pan. */}
      <Rect
        x={left}
        y={top}
        width={right - left}
        height={bottom - top}
        fill="rgba(0,0,0,0.001)"
        listening={!panMode}
        onMouseDown={startStroke}
        onMouseMove={extendStroke}
        onMouseUp={endStroke}
        onMouseLeave={endStroke}
        onTouchStart={startStroke}
        onTouchMove={extendStroke}
        onTouchEnd={endStroke}
      />

      {/* Snap hint guide lines extending from the current pen position. */}
      {!freeAngles && last && hint.horizontal && (
        <Line
          points={[left, last.y, right, last.y]}
          stroke="#2a70f5"
          strokeWidth={1}
          dash={[4, 6]}
          opacity={0.35}
          listening={false}
        />
      )}
      {!freeAngles && last && hint.vertical && (
        <Line
          points={[last.x, top, last.x, bottom]}
          stroke="#2a70f5"
          strokeWidth={1}
          dash={[4, 6]}
          opacity={0.35}
          listening={false}
        />
      )}

      {/* Live raw "ink" — soft, faintly tinted to read as in-progress. */}
      {rawFlat && (
        <Line
          points={rawFlat}
          stroke={freeAngles ? "#7c3aed" : "#0c0c10"}
          strokeWidth={3}
          opacity={0.45}
          lineCap="round"
          lineJoin="round"
          listening={false}
          tension={0.2}
        />
      )}
    </Layer>
  );
}
